import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js/auto/auto.mjs';
import { DashboardService } from 'src/app/core/services/dashboard.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MONTH_LABELS, normalizeInteractionMetrics } from './functions';

interface DistributionData {
  day_hour_distribution: {
    [key: string]: string;
  };
}

interface ActivityTopSlot {
  label: string;
  percent: string;
  color: string;
}

interface ChannelSegment {
  name: string;
  count: number;
  percent: number;
  color: string;
}

@Component({
  selector: 'globot-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
})
export class StatisticsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('calendar') calendar: any;
  rangeDates: any;
  isHandset: boolean = false;

  // KPI data
  data: any = {};

  // Activity bar chart
  hasActivityData: boolean = false;
  activityTopSlots: ActivityTopSlot[] = [];
  public activityBarData: ChartData<'bar'> = { datasets: [] };
  public activityBarOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 9, family: 'Inter, sans-serif' },
          color: '#9ca3af',
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f5f3ff', lineWidth: 1 },
        ticks: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (ctx) => ` ${(ctx.raw as number).toFixed(1)}% del tráfico`,
          title: (items) => items[0]?.label ?? '',
        },
      },
    },
    animation: { duration: 700, easing: 'easeInOutQuart' },
  };

  // Keywords
  keywords: { keyword: string; count: number }[] = [];

  // Channel donut chart
  channelTotal: number = 0;
  channelSegments: ChannelSegment[] = [];
  hasPieChartData: boolean = false;
  public channelDoughnutData: ChartData<'doughnut'> = { datasets: [] };
  public channelDoughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct =
              this.channelTotal > 0
                ? Math.round(((ctx.raw as number) / this.channelTotal) * 100)
                : 0;
            return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
          },
        },
      },
    },
  };

  // Satisfaction gauge
  satisfactionTotal: number = 0;
  satisfactionPositivePercent: number = 0;
  satisfactionNegativePercent: number = 0;
  satisfactionAvg: string = '0.0';
  gaugeColor: string = '#7B5CF5';
  hasGaugeData: boolean = false;

  public gaugeData: ChartData<'doughnut'> = { datasets: [] };
  public gaugeOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    circumference: 180,
    rotation: -90,
    cutout: '60%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    animation: { animateRotate: true, duration: 1000, easing: 'easeInOutQuart' },
  };

  // Monthly line chart
  public monthlyBarData: ChartData<'line'> = { labels: [], datasets: [] };
  public monthlyBarOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: { autoSkip: true, maxRotation: 0, minRotation: 0 },
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
        grid: { color: '#f5f3ff', lineWidth: 1 },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (context: TooltipItem<'line'>) => {
            const findedMonth = MONTH_LABELS.findIndex((month) => context.label.includes(month));
            const monthName = findedMonth >= 0 ? MONTH_LABELS[findedMonth] : context.label;
            return `${monthName}: ${context.formattedValue} conversaciones`;
          },
          title: () => '',
        },
      },
    },
    animation: { duration: 700, easing: 'easeInOutQuart' },
  };
  hasMonthlyBarData: boolean = false;

  botId: string;

  private readonly CHANNEL_COLORS: Record<string, string> = {
    Messenger: '#7B5CF5',
    Instagram: '#A78BFA',
    WhatsApp: '#C4B5FD',
    'Sitio web': '#6366F1',
    Discord: '#818CF8',
    Otros: '#DDD6FE',
  };

  private readonly TIME_SLOTS = [
    { label: '0h-2h', key: '00:00-01:59' },
    { label: '2h-4h', key: '02:00-03:59' },
    { label: '4h-6h', key: '04:00-05:59' },
    { label: '6h-8h', key: '06:00-07:59' },
    { label: '8h-10h', key: '08:00-09:59' },
    { label: '10h-12h', key: '10:00-11:59' },
    { label: '12h-14h', key: '12:00-13:59' },
    { label: '14h-16h', key: '14:00-15:59' },
    { label: '16h-18h', key: '16:00-17:59' },
    { label: '18h-20h', key: '18:00-19:59' },
    { label: '20h-22h', key: '20:00-21:59' },
    { label: '22h-24h', key: '22:00-23:59' },
  ];

  constructor(
    private dashboardService: DashboardService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {
    this.botId = this.aRoute.snapshot.paramMap.get('id') ?? '';
    const sections = this.router.routerState.snapshot.url.split('/');
    this.botId = sections[3];

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.rangeDates = [sevenDaysAgo, currentDate];

    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isHandset = result.matches;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.dashboardService
      .getMonthlyConversationsStats(this.botId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.updateMonthlyChart(res);
        } else {
          this.monthlyBarData = { labels: [], datasets: [] };
          this.hasMonthlyBarData = false;
        }
      });
    this.updateCharts();
  }

  updateCharts() {
    const init = this.formatDate(this.rangeDates[0]);
    const term = this.formatDate(this.rangeDates[1]);
    if (!init) {
      console.error('Las fechas no son válidas.');
      return;
    }

    const endDate = term ?? init;

    this.dashboardService
      .getConversationStats(this.botId, init, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.data = res;
          this.buildChannelData();
        } else {
          this.data = {};
          this.channelSegments = [];
          this.channelTotal = 0;
          this.hasPieChartData = false;
        }
      });

    this.dashboardService
      .getConversationDistributionStats(this.botId, init, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.buildActivityRows(res);
        } else {
          this.hasActivityData = false;
          this.activityTopSlots = [];
          this.activityBarData = { datasets: [] };
        }
      });

    this.dashboardService
      .getFeedbackStats(this.botId, init, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.buildSatisfactionData(res);
        } else {
          this.hasGaugeData = false;
          this.gaugeData = { datasets: [] };
        }
      });

    this.dashboardService
      .getKeywords(this.botId, init, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.keywords = res?.keywords ?? [];
      });
  }

  buildChannelData() {
    const channels = [
      { name: 'Messenger', key: 'fb_count' },
      { name: 'Instagram', key: 'ig_count' },
      { name: 'WhatsApp', key: 'wsp_count' },
      { name: 'Sitio web', key: 'web_count' },
      { name: 'Discord', key: 'discord_count' },
      { name: 'Otros', key: 'others_count' },
    ];

    const withCounts = channels
      .map((ch) => ({
        name: ch.name,
        count: this.data[ch.key] || 0,
        color: this.CHANNEL_COLORS[ch.name],
      }))
      .filter((ch) => ch.count > 0);

    this.channelTotal = withCounts.reduce((sum, ch) => sum + ch.count, 0);
    this.channelSegments = withCounts.map((ch) => ({
      name: ch.name,
      count: ch.count,
      percent: this.channelTotal > 0 ? (ch.count / this.channelTotal) * 100 : 0,
      color: ch.color,
    }));
    this.hasPieChartData = this.channelSegments.length > 0;

    this.channelDoughnutData = {
      labels: withCounts.map((ch) => ch.name),
      datasets: [
        {
          data: withCounts.map((ch) => ch.count),
          backgroundColor: withCounts.map((ch) => ch.color),
          borderWidth: 0,
          hoverOffset: 4,
        } as any,
      ],
    };
  }

  buildActivityRows(response: DistributionData) {
    const dist = response.day_hour_distribution;
    const parsed = this.TIME_SLOTS.map((slot) => ({
      label: slot.label.split('-')[0], // "0h-2h" → "0h"
      fullLabel: slot.label,
      value: parseFloat((dist[slot.key] || '0%').replace('%', '')),
    }));

    const maxValue = Math.max(...parsed.map((s) => s.value), 1);

    // Interpolate #EDE9FE → #7B5CF5 by relative intensity
    const colors = parsed.map((s) => {
      const t = s.value / maxValue;
      const r = Math.round(237 + t * (123 - 237));
      const g = Math.round(233 + t * (92 - 233));
      const b = Math.round(254 + t * (245 - 254));
      return `rgb(${r}, ${g}, ${b})`;
    });

    this.activityBarData = {
      labels: parsed.map((s) => s.label),
      datasets: [
        {
          data: parsed.map((s) => s.value),
          backgroundColor: colors,
          hoverBackgroundColor: colors.map(() => '#5B4BE8'),
          borderRadius: { topLeft: 5, topRight: 5, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          maxBarThickness: 32,
        } as any,
      ],
    };

    this.hasActivityData = parsed.some((s) => s.value > 0);

    // Top 3 slots for the badge legend below the chart
    const sorted = [...parsed].sort((a, b) => b.value - a.value).filter((s) => s.value > 0);
    const badgeColors = ['#7B5CF5', '#A78BFA', '#C4B5FD'];
    this.activityTopSlots = sorted.slice(0, 3).map((s, i) => ({
      label: s.fullLabel,
      percent: `${s.value.toFixed(0)}%`,
      color: badgeColors[i],
    }));
  }

  buildSatisfactionData(response: any) {
    const totalLike = response.total_like || 0;
    const totalDislike = response.total_dislike || 0;
    const totalNull = response.total_null || 0;
    const totalRated = totalLike + totalDislike;
    const totalAll = totalRated + totalNull;

    // Use total (including un-rated) so percentages reflect all interactions
    this.satisfactionTotal = totalAll;

    if (totalRated === 0) {
      this.hasGaugeData = false;
      this.gaugeData = { datasets: [] };
      return;
    }

    // Each % is relative to ALL interactions, not just rated ones
    this.satisfactionPositivePercent = Math.round((totalLike / totalAll) * 100);
    this.satisfactionNegativePercent = Math.round((totalDislike / totalAll) * 100);

    const avg = (totalLike * 5) / totalRated;
    this.satisfactionAvg = avg.toFixed(1);

    this.gaugeColor = '#7B5CF5';

    // 3-segment gauge: positive | negative | null/unrated
    // Give a minimum tip (0.8) so the arc edge is visible even at 0%
    const positiveVal =
      this.satisfactionPositivePercent === 0 ? 0.8 : this.satisfactionPositivePercent;
    const negativeVal = this.satisfactionNegativePercent;
    const nullVal = Math.max(0, 100 - positiveVal - negativeVal);

    this.gaugeData = {
      datasets: [
        {
          data: [positiveVal, negativeVal, nullVal],
          backgroundColor: ['#7B5CF5', '#F87171', '#E5E7EB'],
          borderWidth: 0,
          hoverOffset: 0,
        } as any,
      ],
    };

    this.hasGaugeData = true;
  }

  updateMonthlyChart(response: any) {
    const interactionData = normalizeInteractionMetrics(response);

    if (!interactionData.length) {
      this.monthlyBarData = { labels: [], datasets: [] };
      this.hasMonthlyBarData = false;
      return;
    }

    const sortedData = [...interactionData].sort((a, b) => a.month.localeCompare(b.month));
    const years = sortedData.map((item) => item.month.split('-')[0]);
    const includeYear = new Set(years).size > 1;

    const labels = sortedData.map((item) => {
      const [year, month] = item.month.split('-');
      const monthIndex = parseInt(month, 10) - 1;
      return includeYear ? `${MONTH_LABELS[monthIndex]} ${year}` : MONTH_LABELS[monthIndex];
    });

    const dataPoints = sortedData.map((item) => item.count);

    this.monthlyBarData = {
      labels,
      datasets: [
        {
          label: 'Conversaciones',
          data: dataPoints,
          borderColor: '#7B5CF5',
          backgroundColor: 'rgba(123, 92, 245, 0.08)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: '#7B5CF5',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
        } as any,
      ],
    };

    this.hasMonthlyBarData = dataPoints.some((value) => value > 0);
  }

  formatDate(fechaOriginal: Date): string | null {
    if (fechaOriginal === null) return null;
    const fechaObj = new Date(fechaOriginal);
    const anio = fechaObj.getFullYear();
    const mes = fechaObj.getMonth() + 1;
    const dia = fechaObj.getDate();
    return `${anio}-${mes < 10 ? '0' : ''}${mes}-${dia < 10 ? '0' : ''}${dia}`;
  }

  onClean() {
    this.rangeDates = '';
    this.calendar.overlayVisible = false;
  }

  onDateSelect() {
    if (this.rangeDates && this.rangeDates.length === 2 && this.rangeDates[0]) {
      this.calendar.overlayVisible = false;
      this.updateCharts();
    }
  }

  get dateRangeLabel(): string {
    if (!this.rangeDates?.[0]) return 'Selecciona un rango de fecha';
    const fmt = (d: Date) => {
      if (!d) return '';
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };
    const start = fmt(this.rangeDates[0]);
    const end = this.rangeDates[1] ? ` - ${fmt(this.rangeDates[1])}` : '';
    return `${start}${end}`;
  }

  trackByPeakLabel(_index: number, slot: ActivityTopSlot): string {
    return slot.label;
  }

  trackByKeyword(_index: number, kw: { keyword: string; count: number }): string {
    return kw.keyword;
  }

  trackByChannelName(_index: number, seg: ChannelSegment): string {
    return seg.name;
  }
}
